import { formatCurrency } from 'lib/formatCurrency'
import {
  ArrowRightIcon,
  CalendarCheckIcon,
  CheckCircleIcon,
  ClockCounterClockwiseIcon,
  ArchiveIcon,
  CurrencyDollarIcon,
  HourglassMediumIcon,
  FireIcon,
  SnowflakeIcon,
  ToothIcon,
} from '@phosphor-icons/react'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { PageScroll } from 'app/components/layout/PageScroll'
import { Badge } from 'app/components/ui/badge'
import { Button as UiButton } from 'app/components/ui/button'
import { ContactJson } from 'app/types/entities'
import LocationFilter from 'app/components/table/filters/LocationFilter'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { listDrillHref, queueActionLabel, queueHref } from './queueLink'
import { FAMILY_ACCENT, FAMILY_TINT, QUEUE_BUNDLES, QueueBundleId, QueueFamily, actionPlayFor } from './queueRegistry'
import { displayName, stamp } from './ResponseRow'
import { MyTasksCard } from './MyTasksCard'
import { ResponseQueuePanel } from './ResponseQueuePanel'
import { QueueCardVM, UseActionCenterReturn } from './useActionCenter'
import { SnoozedPopover } from './SnoozedPopover'
import { WeekOpeningsCard } from 'app/components/schedule/WeekOpeningsCard'
import { cn } from 'app/lib/utils'

export type ActionCenterComponentProps = UseActionCenterReturn

/** Fades the dot backdrop in below the greeting and back out above the foot. */
const DOT_FIELD_FADE = `linear-gradient(to bottom, transparent 0px, black 200px, black calc(100% - 140px), transparent 100%)`

/**
 * Explains where the money figure comes from and, just as importantly, what it
 * does not claim. Shown on hover of the value on a queue card.
 */
function ValueExplainer({ queue }: { queue: QueueCardVM }) {
  const { def, perItemValue } = queue
  if (perItemValue == null) return null
  // Styled for the app's dark tooltip surface (bg-foreground/text-background,
  // which flips with theme) rather than overriding it to a white card: every
  // other tooltip in the app is dark.
  return (
    <div className="w-64 px-1 py-1.5 text-left">
      <p className="font-medium text-background">
        {formatCurrency(perItemValue)} {def.valueUnit}
      </p>
      <p className="mt-1.5 leading-relaxed text-background/75">{def.valueBasis}</p>
      <p className="mt-2 border-t border-background/20 pt-2 leading-relaxed text-background/70">
        The value of one contact who books, not of the whole queue. Not every contact converts, so these figures do not
        add up.
      </p>
    </div>
  )
}

// Leads before patients, the order the cards have always read in.
const FAMILY_ORDER: QueueFamily[] = [`lead`, `patient`]

/**
 * Collapse modules that belong to one workflow into a single slot, keeping
 * everything else where it is and preserving registry order.
 */
function bundleEntries(entries: QueueCardVM[]): { bundle?: QueueBundleId; queues: QueueCardVM[] }[] {
  const out: { bundle?: QueueBundleId; queues: QueueCardVM[] }[] = []
  for (const entry of entries) {
    const bundle = entry.def.bundle
    if (!bundle) {
      out.push({ queues: [entry] })
      continue
    }
    const existing = out.find((slot) => slot.bundle === bundle)
    if (existing) existing.queues.push(entry)
    else out.push({ bundle, queues: [entry] })
  }
  return out
}

/**
 * One workflow, several timeframes.
 *
 * Three hygiene cards side by side read as three separate jobs. A practice
 * thinks of it as one job, recall, worked in bands because someone a fortnight
 * late is a different call from someone three years gone. Each band keeps its
 * own count and its own way in, so nothing is lost by sharing a card.
 */
function BundledQueueCard({
  bundle,
  queues,
  userId,
  groupedNavigation,
}: {
  bundle: QueueBundleId
  queues: QueueCardVM[]
  userId: string | undefined
  groupedNavigation: boolean
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const meta = QUEUE_BUNDLES[bundle]
  const first = queues[0]
  const known = queues.filter((queue) => queue.count != null)
  const total = known.reduce((sum, queue) => sum + (queue.count ?? 0), 0)
  const perItemValue = queues.find((queue) => queue.perItemValue != null)?.perItemValue

  return (
    <div
      className={cn(
        `flex h-full flex-col rounded-xl border border-default bg-surface bg-linear-to-b to-transparent to-60% p-6 shadow-elevation-2 transition hover:shadow-elevation-4`,
        FAMILY_TINT[first.def.family],
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn(`rounded-lg p-2.5`, FAMILY_ACCENT[first.def.family])}>
          <ToothIcon size={22} weight="bold" />
        </div>
        <div className="min-w-0">
          <p className="text-ui-lg text-strong">{meta.name}</p>
          <p className="hidden text-body-xs text-subtle md:block">{meta.strapline}</p>
        </div>
      </div>

      {/* One row per band. The count is the whole pitch, and the row is the
          way in, so there is no separate button to hunt for. */}
      <div className="mt-5 flex flex-col">
        {queues.map((queue) => {
          const count = queue.count
          const isLoading = count == null
          const isClear = count === 0
          return (
            <button
              key={queue.def.id}
              type="button"
              disabled={isLoading || isClear}
              onClick={() => navigate(queueHref(queue.def, userId, location.search, groupedNavigation))}
              className={cn(
                `group -mx-2 flex items-center justify-between gap-3 rounded-lg px-2 py-2.5 text-left transition`,
                isLoading || isClear
                  ? `cursor-default`
                  : `hover:bg-surface hover:shadow-elevation-1 hover:ring-1 hover:ring-default`,
              )}
            >
              <span className="min-w-0">
                <span className="block truncate text-ui-sm text-strong">{queue.def.shortName ?? queue.def.name}</span>
                <span className="block truncate text-body-xs text-subtle">
                  {queue.def.definition ?? queue.def.strapline}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                {isLoading ? (
                  <span className="block h-6 w-12 animate-pulse rounded bg-surface-muted" />
                ) : isClear ? (
                  <span className="inline-flex items-center gap-1.5 text-body-sm text-green-700">
                    <CheckCircleIcon size={16} weight="fill" /> Clear
                  </span>
                ) : (
                  <>
                    <span className="gilroy text-xl leading-none tabular-nums text-strong">
                      {count.toLocaleString(`en-US`)}
                    </span>
                    <ArrowRightIcon
                      size={14}
                      weight="bold"
                      className="text-minimal transition group-hover:translate-x-0.5 group-hover:text-subtle"
                    />
                  </>
                )}
              </span>
            </button>
          )
        })}
      </div>

      {/* Absorbs the slack when this card is shorter than its neighbour. A bare
          `mt-auto` on the footer would do it too, but it collapses the gap to
          nothing on the tallest card, where there is no slack to distribute. */}
      <div className="grow" />
      <div className="mt-5 flex items-center justify-between border-t border-subtle pt-4">
        <p className="flex min-h-6 items-center text-body-xs text-subtle">
          <span className="mr-1 tabular-nums text-strong">{total.toLocaleString(`en-US`)}</span> waiting in total
        </p>
        {perItemValue != null && (
          <TooltipWrapper
            placement="top"
            tooltipClassName="max-w-xs rounded-lg text-left shadow-elevation-2"
            message={<ValueExplainer queue={first} />}
          >
            <p className="cursor-help text-body-xs text-subtle">
              <span className="gilroy text-base text-green-700">~{formatCurrency(perItemValue)}</span>{' '}
              {first.def.valueUnit}
            </p>
          </TooltipWrapper>
        )}
      </div>
    </div>
  )
}

/**
 * One titled band for every work card: your tasks first, then the queues,
 * leads before patients. The cards keep their own family tint, so the split
 * still shows at a glance without a header of its own per family. The running
 * total on the right lets you size the queues before reading a single card.
 */
function QueueSection({
  entries,
  userId,
  groupedNavigation,
}: {
  entries: QueueCardVM[]
  userId: string | undefined
  groupedNavigation: boolean
}) {
  const ordered = FAMILY_ORDER.flatMap((family) => entries.filter((entry) => entry.def.family === family))
  const known = ordered.filter((entry) => entry.count != null)
  const waiting = known.reduce((sum, entry) => sum + (entry.count ?? 0), 0)
  return (
    <section className="mt-8 first:mt-6">
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-ui-sm text-strong">
            <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />
            Your work
          </p>
          <p className="mt-0.5 hidden text-body-xs text-subtle md:block">
            Your tasks, then patients and leads that might need your attention
          </p>
        </div>
        {known.length > 0 && (
          <p className="shrink-0 text-body-xs text-subtle">
            <span className="tabular-nums text-strong">{waiting.toLocaleString(`en-US`)}</span> waiting
          </p>
        )}
      </div>
      {/* Hairline that fades out, so it reads as a section break rather than a
          box being drawn around the cards. */}
      <div className="mt-3 h-px bg-gradient-to-r from-blue-500/30 to-transparent" />

      {/* Cards in a row match heights. Sized to their own content they came out
          a few pixels apart, because the count rows and the footer vary, and two
          cards nearly but not quite aligned reads as a mistake rather than as a
          choice. The footer is pushed to the bottom so the slack lands in one
          place instead of stretching the rows. */}
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <MyTasksCard />
        {bundleEntries(ordered).map((entry) =>
          entry.bundle ? (
            <BundledQueueCard
              key={entry.bundle}
              bundle={entry.bundle}
              queues={entry.queues}
              userId={userId}
              groupedNavigation={groupedNavigation}
            />
          ) : (
            <QueueCard
              key={entry.queues[0].def.id}
              queue={entry.queues[0]}
              userId={userId}
              groupedNavigation={groupedNavigation}
            />
          ),
        )}
      </div>
    </section>
  )
}

const QUEUE_ICONS: Record<string, React.ReactNode> = {
  'active-leads': <FireIcon size={22} weight="bold" />,
  'due-for-hygiene': <ToothIcon size={22} weight="bold" />,
  'hygiene-coming-due': <CalendarCheckIcon size={22} weight="bold" />,
  'hygiene-long-past-due': <ClockCounterClockwiseIcon size={22} weight="bold" />,
  // One glyph per treatment band, reading warm to cold, so the bundle's three
  // entries are told apart at a glance the way the hygiene three are.
  'treatment-fresh': <CurrencyDollarIcon size={22} weight="bold" />,
  'treatment-cooling': <HourglassMediumIcon size={22} weight="bold" />,
  'treatment-cold': <SnowflakeIcon size={22} weight="bold" />,
  'treatment-stale': <ArchiveIcon size={22} weight="bold" />,
}

function QueueCard({
  queue,
  userId,
  groupedNavigation,
}: {
  queue: QueueCardVM
  userId: string | undefined
  groupedNavigation: boolean
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { def, count, freshness, preview } = queue
  const isLoading = count == null
  const isClear = count === 0
  const [openContact, setOpenContact] = useState<ContactJson>()

  // Each queue carries its own tint, fading out down the card: enough color to
  // tell them apart at a glance without competing with the numbers.
  return (
    <div
      className={cn(
        `flex h-full flex-col rounded-xl border border-default bg-surface bg-linear-to-b to-transparent to-60% p-6 shadow-elevation-2 transition hover:shadow-elevation-4`,
        FAMILY_TINT[def.family],
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(`rounded-lg p-2.5`, FAMILY_ACCENT[def.family])}>{QUEUE_ICONS[def.id]}</div>
          <div>
            <p className="text-ui-lg text-strong">{def.name}</p>
            <p className="hidden text-body-xs text-subtle md:block">{def.strapline}</p>
          </div>
        </div>
      </div>

      {/* Fixed-height stat row so every card is the same height in every
          state: loading, clear, and populated. */}
      <div className="mt-6 flex h-9 items-end justify-between gap-4">
        {isLoading ? (
          <div className="h-9 w-32 animate-pulse rounded bg-surface-muted" />
        ) : isClear ? (
          <p className="flex items-center gap-2 text-green-700">
            <CheckCircleIcon size={24} weight="fill" />
            <span className="text-ui-lg">Queue clear</span>
          </p>
        ) : (
          <p className="flex items-baseline gap-2">
            <span className="gilroy text-4xl leading-none text-strong">{count.toLocaleString(`en-US`)}</span>
            <span className="text-body-sm text-subtle">waiting</span>
            {freshness
              .filter((entry) => entry.count > 0)
              .map(({ segment, count: segmentCount }) => (
                <TooltipWrapper key={segment.id} placement="top" message={segment.description} className="cursor-help">
                  <Badge color="blue" label={`+${segmentCount.toLocaleString(`en-US`)} ${segment.label}`} />
                </TooltipWrapper>
              ))}
          </p>
        )}
      </div>

      {!!def.previewCount && !isClear && (
        <div className="mt-4 flex flex-col border-t border-subtle pt-3">
          <p className="pb-1 text-caption-sm font-medium uppercase tracking-wide text-subtle">Most recent</p>
          <div className="-mx-2 flex flex-col">
            {preview
              ? preview.map(({ contact, enteredAt }) => (
                  <UiButton key={contact.id} variant="ghost" size="row" onClick={() => setOpenContact(contact)}>
                    <span className="min-w-0 flex-1 truncate text-body-sm text-strong">{displayName({ contact })}</span>
                    <span className="hidden min-w-0 truncate text-body-xs text-subtle sm:block">
                      {actionPlayFor(def.id, contact).label}
                    </span>
                    {enteredAt && (
                      <span className="shrink-0 text-body-xs tabular-nums text-minimal">{stamp(enteredAt)}</span>
                    )}
                  </UiButton>
                ))
              : Array.from({ length: def.previewCount }, (_, row) => (
                  <span key={row} className="mx-2 my-1.5 block h-5 animate-pulse rounded bg-surface-muted" />
                ))}
          </div>
        </div>
      )}

      {/* Absorbs the slack when this card is shorter than its neighbour. A bare
          `mt-auto` on the footer would do it too, but it collapses the gap to
          nothing on the tallest card, where there is no slack to distribute. */}
      <div className="grow" />
      <div className="mt-6 flex items-center justify-between border-t border-subtle pt-4">
        <UiButton
          variant="primary"
          size="sm"
          disabled={isLoading || isClear}
          // Board modules open a board; the rest open the one-at-a-time flow.
          // `queueHref` carries only the location drill-down, never `wId`
          // (see queueNavSearch), and is the same source `queueActionLabel`
          // reads below, so the button can never say one thing and go another.
          onClick={() => navigate(queueHref(def, userId, location.search, groupedNavigation))}
        >
          {queueActionLabel(def, userId)}
          <ArrowRightIcon weight="bold" />
        </UiButton>
        <div className="flex items-center gap-3 text-body-xs text-subtle">
          <SnoozedPopover queueId={def.id} />
          <Link
            to={groupedNavigation ? listDrillHref(def) : def.drillHref}
            className="hover:text-default hover:underline"
          >
            View list
          </Link>
        </div>
      </div>
      {!!def.previewCount && (
        <ContactDrawer
          openDrawer={Boolean(openContact)}
          contact={openContact}
          onClose={() => setOpenContact(undefined)}
          onChange={setOpenContact}
        />
      )}
    </div>
  )
}

export const ActionCenterComponent = observer(function ActionCenterComponent(props: ActionCenterComponentProps) {
  const { user, workspace, greeting, queues } = props
  const groupedNavigation = useFeatureToggle(`grouped-navigation`)

  if (!user) return null

  if (!workspace) {
    return (
      <PageScroll>
        <div className="flex h-full items-center justify-center">
          <p className="text-body-md text-subtle">Select a workspace to see its Action Center.</p>
        </div>
      </PageScroll>
    )
  }

  return (
    <PageScroll contentClassName="pb-14 pt-8">
      <div className="relative">
        {/* Dot matrix backdrop. Negative insets cancel PageScroll's px-10 pt-8
            so the field reaches the window edges instead of ending at the
            content column. The mask fades it in under the header (the greeting
            keeps clean paper) and back out at the foot, so the field never
            terminates on a visible straight edge. Every card above it carries a
            `bg-surface` base under its tint gradient: the tint alone starts at 7%
            alpha, so without the base the dots showed straight through the top
            of each card. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-x-10 -top-8 bottom-0"
          style={{
            backgroundImage: `radial-gradient(circle at center, var(--dot-ink) 1px, transparent 1px)`,
            backgroundSize: `var(--dot-gap) var(--dot-gap)`,
            maskImage: DOT_FIELD_FADE,
            WebkitMaskImage: DOT_FIELD_FADE,
          }}
        />
        <div className="relative mx-auto w-full max-w-5xl">
          {/* The header owns its own row and answers "how are we doing" with
              banked results. The cards below answer "what's next" with what's
              still waiting, so the two never state the same number. */}
          <div>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-body-sm text-subtle">{moment().format(`dddd, MMMM D`)}</p>
                <h3 className="gilroy mt-1 text-strong">{greeting}</h3>
              </div>
              {/* Same standalone Locations button (and states) as the leads table.
                  It sat on a toolbar row of its own below, which held the day's
                  tally on the left until that was removed; alone on that row it
                  was just a floating control, so it rides the greeting line. */}
              <LocationFilter customSearchParam="filters" />
            </div>
            <p className="mt-3 hidden text-body-md leading-relaxed text-default md:block">
              Every contact below is waiting on a call, a text, or a follow-up. Start a queue to work through it one
              person at a time, or open the list if you would rather pick your own.
            </p>
          </div>

          {/* Full width, above both families. It spans leads and patients, so it
              fits inside neither, and it is the only time-critical module here:
              hygiene and treatment can wait until tomorrow, an unanswered
              patient cannot. */}
          <ResponseQueuePanel />

          {/* Above the queues, not under them. Every card below is somebody to put
              in a chair, so what chair time exists frames the lot: a practice can
              work a queue all morning and book nobody if nothing is open. */}
          <WeekOpeningsCard />

          <QueueSection entries={queues} userId={user?.id} groupedNavigation={groupedNavigation} />
        </div>
      </div>
    </PageScroll>
  )
})
